import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import { useToast } from "../context/ToastContext";
import { fetchGeoDirectory } from "../api/geoApi";
import { postLeadsBulk } from "../api/leadsTableApi";
import { extractLeadsPdfText } from "../api/leadsApi";
import { createGeoDirectoryStore } from "../data/geoDirectoryStore";
import type { TelanganaDistrictKey } from "../data/telanganaDistricts";
import { telanganaDistrictLabel } from "../data/telanganaDistricts";
import type { LeadParseStats } from "../utils/leadsImportParse";
import { parseLeadExportText } from "../utils/leadsImportParse";
import { parseLeadExcelWorkbook } from "../utils/leadsImportExcel";
import { matchParsedLeadsToGeo, type MatchedLeadRow, type UnmatchedLeadRow } from "../utils/leadsImportGeoMatch";
import { ensureMissingLeadVillages, hasMissingVillageUnmatched } from "../utils/leadsImportEnsureVillages";
import { buildLeadsTablePayloadFromMatched } from "../utils/leadsImportToLeadsTable";
import leadsImportTemplateUrl from "url:../templates/leads-import-template.xlsx";

type LeadsImportPanelProps = {
  /** Called after rows are saved to MySQL `Leads` so the list can refresh. */
  onSaved?: () => void;
};

/** PDF/Excel lead import UI — used on the Leads page. */
export function LeadsImportPanel({ onSaved }: LeadsImportPanelProps) {
  const { store: geoStore, loading: geoLoading, error: geoError, refetch } = useGeoDirectory();
  const { showToast } = useToast();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileLabel, setFileLabel] = useState("");
  const [matched, setMatched] = useState<MatchedLeadRow[]>([]);
  const [unmatched, setUnmatched] = useState<UnmatchedLeadRow[]>([]);
  const [parsedDistrict, setParsedDistrict] = useState<TelanganaDistrictKey | null>(null);
  const [autoCreateVillages, setAutoCreateVillages] = useState(true);
  const [ensureInfo, setEnsureInfo] = useState<string | null>(null);
  const [ensureErrors, setEnsureErrors] = useState<string[]>([]);
  const [parseStats, setParseStats] = useState<LeadParseStats | null>(null);

  useEffect(() => {
    if (!geoStore) setAutoCreateVillages(false);
  }, [geoStore]);

  const runParse = useCallback(
    async (file: File) => {
      setBusy(true);
      setError(null);
      setFileLabel(file.name);
      setMatched([]);
      setUnmatched([]);
      setParsedDistrict(null);
      setEnsureInfo(null);
      setEnsureErrors([]);
      setParseStats(null);
      try {
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
        let parsedRows: ReturnType<typeof parseLeadExportText>["rows"] = [];
        let distFromFile: TelanganaDistrictKey | null = null;

        const noDefaultDistrict = null;
        if (ext === "pdf") {
          const text = await extractLeadsPdfText(file);
          const pr = parseLeadExportText(text, { geo: geoStore, defaultDistrictKey: noDefaultDistrict });
          parsedRows = pr.rows;
          distFromFile = pr.districtKey;
          setParseStats(pr.stats);
        } else if (ext === "xlsx" || ext === "xls") {
          const buf = await file.arrayBuffer();
          const pr = parseLeadExcelWorkbook(buf, { defaultDistrictKey: noDefaultDistrict });
          parsedRows = pr.rows;
          distFromFile = pr.districtKey;
          setParseStats(pr.stats);
        } else {
          throw new Error("Use a .pdf, .xlsx, or .xls file.");
        }

        setParsedDistrict(distFromFile);
        if (parsedRows.length === 0) {
          throw new Error("No data rows were found. Check headers (Mandal, Village, Application No, Applicant, etc.).");
        }
        let { matched: m, unmatched: u } = matchParsedLeadsToGeo(geoStore, distFromFile, parsedRows);

        if (autoCreateVillages && geoStore && hasMissingVillageUnmatched(u)) {
          const ens = await ensureMissingLeadVillages(geoStore, distFromFile, u);
          const parts: string[] = [];
          if (ens.created > 0) {
            parts.push(`Added ${ens.created} new mandal/village name(s) in the geo directory where missing`);
          }
          if (ens.existed > 0) parts.push(`${ens.existed} already present (skipped)`);
          setEnsureInfo(parts.length ? parts.join(" · ") : null);
          setEnsureErrors(ens.errors);
          await refetch();
          const freshStore = createGeoDirectoryStore(await fetchGeoDirectory());
          const second = matchParsedLeadsToGeo(freshStore, distFromFile, parsedRows);
          m = second.matched;
          u = second.unmatched;
        }

        setMatched(m);
        setUnmatched(u);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [geoStore, autoCreateVillages, refetch],
  );

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) void runParse(f);
  };

  const doImport = async () => {
    if (matched.length === 0) return;
    const rows = buildLeadsTablePayloadFromMatched(matched);
    if (!rows.length) {
      showToast("No rows with an application number to save.", "error");
      return;
    }
    setBusy(true);
    try {
      const { count } = await postLeadsBulk(rows);
      showToast(`Saved ${count} row(s) to the Leads table (Lead_ID = application no).`, "success");
      onSaved?.();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not save to the Leads table.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="card card--gov card--elevated" style={{ marginBottom: "1.25rem" }}>
        <p className="section-lead section-lead--tight text-muted" style={{ marginTop: 0 }}>
          Import <strong>Excel</strong> (e.g. SL No, Mandal, <strong>District</strong>, Village, <strong>Application No</strong>, Applicant,
          Mobile, Aadhaar, list type, status) or TG-style <strong>PDF</strong> exports. The <strong>Name of the Constituency</strong>{" "}
          column is ignored. Mandals and villages are matched to the{" "}
          <Link to="/geo-directory">geo directory</Link> (with fuzzy village matching). PDFs should include a line like{" "}
          <span className="mono">District - Nagarkurnool</span> when there is no per-row district. Saving writes only to the{" "}
          MySQL <strong>Leads</strong> table; <strong>Lead_ID</strong> is the application number from the file.
        </p>

        {geoLoading ? (
          <p className="text-muted">Loading geo directory…</p>
        ) : geoError ? (
          <p className="alert alert--danger" role="alert">
            Geo directory unavailable: {geoError}. Imports cannot match villages until this loads.
          </p>
        ) : null}

        <label
          className={`goods-receipts-filters__field${!geoStore ? " text-muted" : ""}`}
          style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.75rem" }}
        >
          <input
            type="checkbox"
            checked={autoCreateVillages}
            disabled={!geoStore}
            onChange={(e) => setAutoCreateVillages(e.target.checked)}
          />
          <span className="goods-receipts-filters__label" style={{ margin: 0 }}>
            After matching, if the mandal or village from the file is still missing under that row&apos;s District in the geo
            directory, add them automatically (only under that row&apos;s district).
            {!geoStore ? " Requires geo directory admin access on your account." : ""}
          </span>
        </label>

        <div className="toolbar-row" style={{ marginTop: "1rem", flexWrap: "wrap", gap: "0.75rem", alignItems: "center" }}>
          <a
            href={leadsImportTemplateUrl}
            className="btn btn--secondary btn--tg"
            download="Leads-import-template.xlsx"
          >
            Download Excel template (blank)
          </a>
          <span className="text-muted section-lead--tight" style={{ margin: 0, maxWidth: "36rem" }}>
            Same column layout as the reference leads Excel (headers only, no data). Fill in rows, then import below.
          </span>
        </div>

        <label className="goods-receipts-filters__field" style={{ marginTop: "1rem", display: "block", maxWidth: "28rem" }}>
          <span className="goods-receipts-filters__label">Import file</span>
          <input
            type="file"
            className="form-control"
            accept=".pdf,.xlsx,.xls,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            disabled={busy}
            onChange={onFile}
          />
        </label>
        {fileLabel ? (
          <p className="text-muted section-lead--tight">
            Last file: <strong>{fileLabel}</strong>
            {parsedDistrict ? (
              <>
                {" "}
                · Single district in file: <strong>{telanganaDistrictLabel(parsedDistrict)}</strong>
              </>
            ) : parseStats && parseStats.rowsAccepted > 0 ? (
              <> · District: read from each row&apos;s <strong>District</strong> column (or from the PDF header line).</>
            ) : null}
          </p>
        ) : null}
        {parseStats ? (
          <p className="text-muted section-lead--tight" style={{ marginTop: "0.35rem" }}>
            Parsed from file: <strong>{parseStats.rowsAccepted}</strong> data row(s) ·{" "}
            <span className="mono" style={{ fontSize: "0.9em" }}>
              lines {parseStats.fileLineCount}
            </span>
            {parseStats.looseLineFallbacks > 0 ? (
              <>
                {" "}
                · <strong>{parseStats.looseLineFallbacks}</strong> recovered via space-glue fallback (PDF)
              </>
            ) : null}
            {parseStats.skippedNoAppToken > 0 ||
            parseStats.skippedNoMandalVillage > 0 ||
            parseStats.skippedShortApplicant > 0 ||
            parseStats.skippedRequiredBlank > 0 ? (
              <>
                {" "}
                · Skipped: no app pattern {parseStats.skippedNoAppToken}, no mandal/village{" "}
                {parseStats.skippedNoMandalVillage}, short name {parseStats.skippedShortApplicant}, blank required fields{" "}
                {parseStats.skippedRequiredBlank}
              </>
            ) : null}
          </p>
        ) : null}
        {busy ? <p className="text-muted">Reading file…</p> : null}
        {ensureInfo ? <p className="text-muted section-lead--tight">{ensureInfo}</p> : null}
        {ensureErrors.length > 0 ? (
          <div className="alert alert--danger" role="alert">
            <strong>Geo directory API issues:</strong>
            <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.25rem" }}>
              {ensureErrors.slice(0, 15).map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {error ? (
          <p className="alert alert--danger" role="alert">
            {error}
          </p>
        ) : null}

        {!busy && matched.length + unmatched.length > 0 ? (
          <>
            <p className="section-lead">
              <strong>{matched.length}</strong> row(s) matched directory villages ·{" "}
              <strong>{unmatched.length}</strong> could not be matched.
            </p>
            <div className="toolbar-row" style={{ marginBottom: "1rem" }}>
              <button
                type="button"
                className="btn btn--primary btn--tg btn--lg"
                disabled={matched.length === 0 || busy}
                onClick={() => void doImport()}
              >
                Save matched rows to Leads table
              </button>
            </div>
            <p className="text-muted section-lead--tight" style={{ marginTop: 0 }}>
              Rows are upserted into <strong>Leads</strong> by application number (<strong>Lead_ID</strong>). Re-importing
              refreshes District, Mandal, Village, Applicant Name, Mobile and Aadhaar from the file, but{" "}
              <strong>keeps the existing Status</strong> for any application that is already in the list (a blank existing
              status will be filled from the file).
            </p>
          </>
        ) : null}
      </div>

      {unmatched.length > 0 ? (
        <div className="card card--gov card--elevated" style={{ marginBottom: "1.25rem" }}>
          <h3 className="form-section-title" style={{ marginTop: 0 }}>
            Unmatched rows ({unmatched.length})
          </h3>
          <p className="text-muted section-lead--tight">
            Correct village spelling to match the IGRS directory under the mandal, or add the village in Geo directory.
          </p>
          <div style={{ overflowX: "auto", maxHeight: "22rem", overflowY: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Mandal (file)</th>
                  <th>Village (file)</th>
                  <th>Applicant</th>
                  <th>Application ref</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {unmatched.slice(0, 200).map((r, i) => (
                  <tr key={`${r.applicationSerial}-${r.applicationRef}-${i}`}>
                    <td>{r.fileMandal}</td>
                    <td>{r.fileVillage}</td>
                    <td>{r.applicantName}</td>
                    <td className="mono">{r.applicationRef}</td>
                    <td className="text-muted">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {unmatched.length > 200 ? <p className="text-muted">Showing first 200 unmatched.</p> : null}
          </div>
        </div>
      ) : null}

      {matched.length > 0 ? (
        <div className="card card--gov card--elevated">
          <h3 className="form-section-title" style={{ marginTop: 0 }}>
            Preview — matched ({Math.min(matched.length, 50)} of {matched.length})
          </h3>
          <div style={{ overflowX: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>District</th>
                  <th>Mandal</th>
                  <th>Village (directory)</th>
                  <th>Applicant</th>
                  <th>Mobile</th>
                  <th>App ref</th>
                  <th>Match</th>
                </tr>
              </thead>
              <tbody>
                {matched.slice(0, 50).map((r, i) => (
                  <tr key={`${r.applicationSerial}-${r.applicationRef}-${i}`}>
                    <td>{telanganaDistrictLabel(r.districtKey)}</td>
                    <td>{r.canonicalMandal}</td>
                    <td>{r.canonicalVillage}</td>
                    <td>{r.applicantName}</td>
                    <td className="mono">{r.mobile}</td>
                    <td className="mono">{r.applicationRef}</td>
                    <td className="text-muted">{r.villageMatchedFuzzy ? "Fuzzy village" : "Exact / list"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}
